export type ApiMode = 'TWO_PLAYER' | 'FOUR_PLAYER';

export interface GuestResponse {
  userId: string;
  guestToken: string;
  displayName: string;
}

export interface CreateGameResponse {
  gameId: string;
  inviteCode: string;
  seat: number;
  state: any;
}

export interface JoinGameResponse {
  gameId: string;
  seat: number;
  team: number;
  state: any;
  status: 'WAITING' | 'PLAYING' | 'ENDED';
}

export interface LookupGameResponse {
  gameId: string;
  mode: ApiMode;
  status: 'WAITING' | 'PLAYING' | 'ENDED';
  takenSeats: number[];
  state?: any;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizeHttpOrigin(value: string | undefined): string {
  const raw = (value || '').trim();
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw)) return '';
  try {
    return trimTrailingSlash(new URL(raw).origin);
  } catch {
    return '';
  }
}

function isLoopbackHostname(hostname: string): boolean {
  const h = (hostname || '').trim().toLowerCase();
  return h === '127.0.0.1' || h === 'localhost';
}

function isLoopbackOrigin(origin: string): boolean {
  try {
    return isLoopbackHostname(new URL(origin).hostname);
  } catch {
    return false;
  }
}

const defaultApiBase = `${window.location.protocol}//${window.location.hostname}:5001`;
const apiEnv = (process.env.REACT_APP_API_URL || '').trim();
const socketEnv = (process.env.REACT_APP_SOCKET_URL || '').trim();

const normalizedApiEnv = normalizeHttpOrigin(apiEnv);
const shouldIgnoreLoopbackEnv =
  Boolean(normalizedApiEnv)
  && isLoopbackOrigin(normalizedApiEnv)
  && !isLoopbackHostname(window.location.hostname);

const API_BASE = apiEnv === 'same-origin'
  ? ''
  : ((shouldIgnoreLoopbackEnv ? '' : normalizedApiEnv) || defaultApiBase);

const SOCKET_BASE = socketEnv === 'same-origin'
  ? window.location.origin
  : (normalizeHttpOrigin(socketEnv) || API_BASE || window.location.origin);

async function request<T>(path: string, method = 'GET', body?: unknown, guestToken?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(guestToken ? { Authorization: `Bearer ${guestToken}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'network error';
    throw new Error(`Cannot reach backend at ${API_BASE || 'same-origin /api'} (${reason}). Start backend and verify REACT_APP_API_URL/REACT_APP_SOCKET_URL.`);
  }

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.message || `Request failed: ${res.status}`);
  }
  return data as T;
}

export function createOrResumeGuest(displayName?: string, guestToken?: string): Promise<GuestResponse> {
  return request<GuestResponse>('/api/user/guest', 'POST', { displayName, guestToken });
}

export function createGame(guestToken: string, mode: ApiMode): Promise<CreateGameResponse> {
  return request<CreateGameResponse>('/api/game', 'POST', { mode }, guestToken);
}

export function lookupGameByCode(guestToken: string, inviteCode: string): Promise<LookupGameResponse> {
  return request<LookupGameResponse>(`/api/game/by-code/${encodeURIComponent(inviteCode)}`, 'GET', undefined, guestToken);
}

export function joinGame(guestToken: string, inviteCode: string, seat?: number): Promise<JoinGameResponse> {
  return request<JoinGameResponse>('/api/game/join', 'POST', { inviteCode, seat }, guestToken);
}

export function postMove(guestToken: string, gameId: string, move: unknown): Promise<{ state: any; status: string }> {
  return request(`/api/game/${encodeURIComponent(gameId)}/move`, 'POST', { move }, guestToken);
}

export function restartGame(guestToken: string, gameId: string): Promise<{ state: any; status: string }> {
  return request(`/api/game/${encodeURIComponent(gameId)}/restart`, 'POST', {}, guestToken);
}

export function postTrainingGame(guestToken: string, trainingGame: unknown): Promise<{ ok: true }> {
  return request(`/api/training/games`, 'POST', trainingGame, guestToken);
}

export function postAiProfile(guestToken: string, profile: unknown): Promise<{ ok: true }> {
  return request(`/api/training/ai-profile`, 'POST', profile, guestToken);
}

export function fetchLatestAiProfile(guestToken: string): Promise<{ profile: any | null }> {
  return request(`/api/training/ai-profile/latest`, 'GET', undefined, guestToken);
}

export function fetchGameState(guestToken: string, gameId: string): Promise<{ state: any; status: 'WAITING' | 'PLAYING' | 'ENDED'; yourSeat: number; yourTeam: number }> {
  return request(`/api/game/${encodeURIComponent(gameId)}`, 'GET', undefined, guestToken);
}

export function clearAllGames(guestToken: string): Promise<{ ok: true; deletedGames: number }> {
  return request(`/api/admin/clear-games`, 'POST', {}, guestToken);
}

export const OnlineApiConfig = {
  apiBase: API_BASE,
  socketBase: SOCKET_BASE,
};
